import { format } from 'date-fns';
import TableContainer from '@mui/material/TableContainer';
import { Table } from '@components/Table';
import Button from '@components/Button';
import LockedButton from '@features/LockedButton';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { PRICING_DRAWER_TYPE } from '@constants/pricing-drawer-ui';
import { ROLE, ACCESS_LEVEL } from '@constants/user';
import { FORM_TYPE } from '../EditModal';

const GetUsersColumnNames = () => {
    const { t } = useTranslation();
    return [
        { key: 0, component: <Typography variant="subtitle2">{t('common.email')}</Typography> },
        {
            key: 1,
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.fullName')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('SettingsUsers.role')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('SettingsUsers.accessLevel')}
                </Typography>
            ),
        },
        {
            key: 4,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('SettingsUsers.addedOn')}
                </Typography>
            ),
        },
        {
            key: 5,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('SettingsUsers.lastLogin')}
                </Typography>
            ),
        },
        { key: 6, component: <Typography align="left" variant="subtitle2"></Typography> },
    ];
};

const getRowsData = (account, currentUser) => {
    const { t } = useTranslation();
    return (account?.users || []).map(user => {
        const userIAM = user.id === currentUser.id;
        const userIsAccountOwner = user.id === account.ownerId;
        let hasAccess = false;
        if (currentUser.role === ROLE.SUPER_ADMIN && !userIAM && !userIsAccountOwner) {
            hasAccess = true;
        }

        const RoleMap = {
            [ROLE.SUPER_ADMIN]: t('SettingsUsers.superAdmin'),
            [ROLE.ADMIN]: t('SettingsUsers.admin'),
            [ROLE.USER]: t('SettingsUsers.user'),
        };
        const AccessLevelMap = {
            [ACCESS_LEVEL.GLOBAL]: t('SettingsUsers.global'),
            [ACCESS_LEVEL.BRAND]: t('common.brand'),
            [ACCESS_LEVEL.LOCATION]: t('common.location'),
        };
        return [
            {
                component: (
                    <Typography variant="subtitle2" align="left">
                        {user.email}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">{`${user?.firstName} ${user?.lastName}`}</Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {RoleMap[user.role]}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {AccessLevelMap[user.accessLevel]}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {format(new Date(user.registrationDate), 'dd/MM/yyyy')}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {format(new Date(user.lastVisit), 'dd/MM/yyyy')}
                    </Typography>
                ),
            },
            {
                value: !hasAccess ? null : user,
            },
        ];
    });
};

const UserTable = ({ currentUser, account, setSelectedUser, setDeleteModalOpen, setModal }) => {
    const { t } = useTranslation();

    const limitExceeded = account?.users?.length >= account?.usersMaxCount;

    return (
        <>
            <TableContainer>
                <Table
                    columnNames={GetUsersColumnNames()}
                    rows={getRowsData(account, currentUser)}
                    onPrimaryAction={({ value }) => {
                        setSelectedUser(value);
                        setModal(prev => ({ ...prev, open: true, type: FORM_TYPE.EDIT }));
                    }}
                    onSecondaryAction={({ value }) => {
                        setSelectedUser(value);
                        setDeleteModalOpen(true);
                    }}
                    isInteractive
                />
            </TableContainer>
            {currentUser.role === ROLE.SUPER_ADMIN && (
                <div style={{ padding: '24px 0' }}>
                    {limitExceeded || account?.billingDiffInSeconds < 0 ? (
                        <LockedButton tooltipType={1} variant="contained" drawerType={PRICING_DRAWER_TYPE.CHANGE}>
                            {t('buttons.addUser')}
                        </LockedButton>
                    ) : (
                        <Button
                            type="button"
                            variant="contained"
                            onClick={() => {
                                setSelectedUser(null);
                                setModal(prev => ({ ...prev, open: true, type: FORM_TYPE.NEW }));
                            }}
                        >
                            {t('buttons.addUser')}
                        </Button>
                    )}
                </div>
            )}
        </>
    );
};

export default UserTable;
